// ---------------------------------------------------------------------------
// System report data service — field catalog + preview data for the
// data report builder. Reads exclusively from Dexie (local-first).
// ---------------------------------------------------------------------------

import { getSystemDb } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface FieldDefinition {
  field: string;       // dot-notation key, e.g. "tenants.name"
  label: string;       // human-readable label
  type: 'string' | 'number' | 'date' | 'boolean';
  category: string;    // group header shown in the field palette
}

// ---------------------------------------------------------------------------
// Static field catalog — describes the queryable fields across system
// DB collections. Keyed as <dexieTable>.<fieldName> so the preview
// renderer can look up values directly from Dexie documents.
// ---------------------------------------------------------------------------
const FIELD_CATALOG: FieldDefinition[] = [
  // Departments (tenants)
  { field: 'tenants.name',         label: 'Department Name',    type: 'string',  category: 'Departments' },
  { field: 'tenants.subdomain',    label: 'Subdomain',          type: 'string',  category: 'Departments' },
  { field: 'tenants.status',       label: 'Status',             type: 'string',  category: 'Departments' },
  { field: 'tenants.tier',         label: 'Tier',               type: 'string',  category: 'Departments' },
  { field: 'tenants.state',        label: 'State',              type: 'string',  category: 'Departments' },
  { field: 'tenants.city',         label: 'City',               type: 'string',  category: 'Departments' },
  { field: 'tenants.nerisId',      label: 'NERIS ID',           type: 'string',  category: 'Departments' },
  { field: 'tenants.createdAt',    label: 'Created At',         type: 'date',    category: 'Departments' },
  { field: 'tenants.updatedAt',    label: 'Updated At',         type: 'date',    category: 'Departments' },

  // Organizations
  { field: 'organizations.name',       label: 'Organization Name',  type: 'string',  category: 'Organizations' },
  { field: 'organizations.status',     label: 'Status',             type: 'string',  category: 'Organizations' },
  { field: 'organizations.type',       label: 'Type',               type: 'string',  category: 'Organizations' },
  { field: 'organizations.createdAt',  label: 'Created At',         type: 'date',    category: 'Organizations' },
  { field: 'organizations.updatedAt',  label: 'Updated At',         type: 'date',    category: 'Organizations' },

  // System Users
  { field: 'system_users.displayName', label: 'User Name',          type: 'string',  category: 'System Users' },
  { field: 'system_users.email',       label: 'Email',              type: 'string',  category: 'System Users' },
  { field: 'system_users.status',      label: 'Status',             type: 'string',  category: 'System Users' },
  { field: 'system_users.role',        label: 'Role',               type: 'string',  category: 'System Users' },
  { field: 'system_users.lastLoginAt', label: 'Last Login',         type: 'date',    category: 'System Users' },
  { field: 'system_users.createdAt',   label: 'Created At',         type: 'date',    category: 'System Users' },
  { field: 'system_users.updatedAt',   label: 'Updated At',         type: 'date',    category: 'System Users' },

  // System Roles
  { field: 'system_roles.name',        label: 'Role Name',          type: 'string',  category: 'System Roles' },
  { field: 'system_roles.description', label: 'Description',        type: 'string',  category: 'System Roles' },
  { field: 'system_roles.status',      label: 'Status',             type: 'string',  category: 'System Roles' },

  // Tiers
  { field: 'tiers.code',           label: 'Tier Code',          type: 'string',  category: 'Tiers' },
  { field: 'tiers.name',           label: 'Tier Name',          type: 'string',  category: 'Tiers' },
  { field: 'tiers.description',    label: 'Description',        type: 'string',  category: 'Tiers' },
  { field: 'tiers.isActive',       label: 'Active',             type: 'boolean', category: 'Tiers' },

  // Feature Flags
  { field: 'feature_flags.key',        label: 'Flag Key',           type: 'string',  category: 'Feature Flags' },
  { field: 'feature_flags.status',     label: 'Status',             type: 'string',  category: 'Feature Flags' },
  { field: 'feature_flags.name',       label: 'Name',               type: 'string',  category: 'Feature Flags' },
  { field: 'feature_flags.updatedAt',  label: 'Updated At',         type: 'date',    category: 'Feature Flags' },

  // Modules
  { field: 'modules.code',         label: 'Module Code',        type: 'string',  category: 'Modules' },
  { field: 'modules.name',         label: 'Module Name',        type: 'string',  category: 'Modules' },
  { field: 'modules.status',       label: 'Status',             type: 'string',  category: 'Modules' },
  { field: 'modules.updatedAt',    label: 'Updated At',         type: 'date',    category: 'Modules' },

  // Import Templates
  { field: 'import_templates.name',        label: 'Template Name',      type: 'string',  category: 'Import Templates' },
  { field: 'import_templates.dataMode',    label: 'Data Mode',          type: 'string',  category: 'Import Templates' },
  { field: 'import_templates.visibility',  label: 'Visibility',         type: 'string',  category: 'Import Templates' },
  { field: 'import_templates.status',      label: 'Status',             type: 'string',  category: 'Import Templates' },
  { field: 'import_templates.createdAt',   label: 'Created At',         type: 'date',    category: 'Import Templates' },
  { field: 'import_templates.updatedAt',   label: 'Updated At',         type: 'date',    category: 'Import Templates' },

  // Import Runs
  { field: 'import_runs.status',       label: 'Run Status',         type: 'string',  category: 'Import Runs' },
  { field: 'import_runs.tenantId',     label: 'Tenant ID',          type: 'string',  category: 'Import Runs' },
  { field: 'import_runs.startedAt',    label: 'Started At',         type: 'date',    category: 'Import Runs' },
  { field: 'import_runs.completedAt',  label: 'Completed At',       type: 'date',    category: 'Import Runs' },
  { field: 'import_runs.recordCount',  label: 'Record Count',       type: 'number',  category: 'Import Runs' },

  // Report Definitions
  { field: 'report_definitions.name',       label: 'Report Name',        type: 'string',  category: 'Reports' },
  { field: 'report_definitions.type',       label: 'Type',               type: 'string',  category: 'Reports' },
  { field: 'report_definitions.status',     label: 'Status',             type: 'string',  category: 'Reports' },
  { field: 'report_definitions.createdBy',  label: 'Created By',         type: 'string',  category: 'Reports' },
  { field: 'report_definitions.createdAt',  label: 'Created At',         type: 'date',    category: 'Reports' },
  { field: 'report_definitions.updatedAt',  label: 'Updated At',         type: 'date',    category: 'Reports' },

  // Audit Logs
  { field: 'audit_logs.action',        label: 'Action',             type: 'string',  category: 'Audit Logs' },
  { field: 'audit_logs.collection',    label: 'Collection',         type: 'string',  category: 'Audit Logs' },
  { field: 'audit_logs.documentId',    label: 'Document ID',        type: 'string',  category: 'Audit Logs' },
  { field: 'audit_logs.actorEmail',    label: 'Actor Email',        type: 'string',  category: 'Audit Logs' },
  { field: 'audit_logs.createdAt',     label: 'Created At',         type: 'date',    category: 'Audit Logs' },
];

// ---------------------------------------------------------------------------
// Mapping from field prefix (Dexie table name) to the table on SystemDatabase
// ---------------------------------------------------------------------------
const COLLECTION_TABLE_MAP: Record<string, string> = {
  tenants:             'tenants',
  organizations:       'organizations',
  system_users:        'system_users',
  system_roles:        'system_roles',
  tiers:               'tiers',
  feature_flags:       'feature_flags',
  modules:             'modules',
  import_templates:    'import_templates',
  import_runs:         'import_runs',
  report_definitions:  'report_definitions',
  audit_logs:          'audit_logs',
};

// ---------------------------------------------------------------------------
// getAvailableFields — returns the full static field catalog
// ---------------------------------------------------------------------------
export async function getAvailableFields(): Promise<FieldDefinition[]> {
  return FIELD_CATALOG;
}

// ---------------------------------------------------------------------------
// getCollectionCounts — returns document counts per collection from Dexie
// ---------------------------------------------------------------------------
export async function getCollectionCounts(): Promise<Record<string, number>> {
  const db = await getSystemDb();
  const counts: Record<string, number> = {};

  await Promise.allSettled(
    Object.entries(COLLECTION_TABLE_MAP).map(async ([prefix, tableName]) => {
      try {
        const col = (db as any)[tableName];
        if (col) {
          const all: Record<string, any>[] = await col.toArray();
          counts[prefix] = all.filter((d) => !d._deleted).length;
        }
      } catch {
        counts[prefix] = 0;
      }
    }),
  );

  return counts;
}

// ---------------------------------------------------------------------------
// fetchPreviewData — reads from Dexie and projects the requested fields
// into flat dot-notation objects suitable for the preview table.
// ---------------------------------------------------------------------------
export async function fetchPreviewData(
  fields: string[],
  limit = 50,
): Promise<Record<string, any>[]> {
  if (fields.length === 0) return [];

  // Group fields by their collection prefix
  const byCollection: Record<string, string[]> = {};
  for (const field of fields) {
    const prefix = field.split('.')[0];
    if (!byCollection[prefix]) byCollection[prefix] = [];
    byCollection[prefix].push(field);
  }

  const db = await getSystemDb();

  // Collect rows per collection, then merge into flat objects
  // If multiple collections are needed we produce a cross-product limited to `limit` rows.
  // For the common single-collection case this is simply a flat projection.
  const collectionRows: Record<string, Record<string, any>[]> = {};

  await Promise.allSettled(
    Object.entries(byCollection).map(async ([prefix, prefixFields]) => {
      const tableName = COLLECTION_TABLE_MAP[prefix];
      if (!tableName) return;
      const col = (db as any)[tableName];
      if (!col) return;

      const all: Record<string, any>[] = await col.toArray();
      const active = all.filter((d) => !d._deleted);
      const sliced = active.slice(0, limit);

      collectionRows[prefix] = sliced.map((doc) => {
        const row: Record<string, any> = {};
        for (const field of prefixFields) {
          const key = field.split('.').slice(1).join('.');
          row[field] = getNestedValue(doc, key);
        }
        return row;
      });
    }),
  );

  // If only one collection — return those rows directly
  const prefixes = Object.keys(collectionRows);
  if (prefixes.length === 0) return [];
  if (prefixes.length === 1) return collectionRows[prefixes[0]];

  // Multiple collections — zip by index (preview only, not a real JOIN)
  const maxRows = Math.min(limit, Math.max(...prefixes.map((p) => collectionRows[p].length)));
  const result: Record<string, any>[] = [];
  for (let i = 0; i < maxRows; i++) {
    const row: Record<string, any> = {};
    for (const prefix of prefixes) {
      const rows = collectionRows[prefix];
      const srcRow = rows[i % rows.length] ?? {};
      Object.assign(row, srcRow);
    }
    result.push(row);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Utility: resolve a dot-notation key from a plain object
// ---------------------------------------------------------------------------
function getNestedValue(obj: Record<string, any>, key: string): any {
  const parts = key.split('.');
  let cur: any = obj;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[part];
  }
  return cur ?? null;
}

// ---------------------------------------------------------------------------
// AI Chatbot Methods — fetch L1 Schema Registry for template generation
// ---------------------------------------------------------------------------

/**
 * Fetches L1 Schema Registry entries from /api/ai/schema for AI template generation.
 * Returns field definitions for tenant data collections (inspections, cases, entities, etc.)
 * rather than system admin collections.
 */
export async function getSchemaFieldsForAI(): Promise<FieldDefinition[]> {
  try {
    const response = await apiFetch('/api/ai/schema');
    const schemas = response.entries || [];
    const fields: FieldDefinition[] = [];
    
    // Convert L1 schema entries into field definitions
    for (const schema of schemas) {
      const collectionName = schema.collectionName || schema.collection || schema.name;
      // Use formatted collection name as category (e.g., "inspectionSeries" → "Inspection Series")
      const category = formatCollectionName(collectionName);
      
      if (schema.fields && Array.isArray(schema.fields)) {
        for (const field of schema.fields) {
          fields.push({
            field: `${collectionName}.${field.path || field.name}`,
            label: field.label || field.name || field.path,
            type: mapSchemaTypeToFieldType(field.type),
            category,
          });
        }
      }
    }
    
    return fields;
  } catch (err) {
    console.error('[systemReportDataService] Failed to fetch schema fields for AI:', err);
    return [];
  }
}

/**
 * Formats a camelCase collection name into a clean Title Case label
 * Examples: "inspectionSeries" → "Inspection Series", "cases" → "Cases"
 */
function formatCollectionName(name: string): string {
  if (!name) return 'Unknown';
  
  // Split camelCase into words
  const words = name.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  
  // Capitalize first letter of each word
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

/**
 * Maps L1 schema field types to FieldDefinition types
 */
function mapSchemaTypeToFieldType(schemaType: string): 'string' | 'number' | 'date' | 'boolean' {
  const normalized = schemaType.toLowerCase();
  if (normalized.includes('number') || normalized.includes('int') || normalized.includes('decimal')) {
    return 'number';
  }
  if (normalized.includes('date') || normalized.includes('time')) {
    return 'date';
  }
  if (normalized.includes('bool')) {
    return 'boolean';
  }
  return 'string';
}

/**
 * Returns mock/sample preview data for AI-generated templates when system-web
 * cannot provide real tenant data for the selected schema fields.
 */
export async function getSchemaPreviewForAI(fields: string[]): Promise<Record<string, any>[]> {
  if (fields.length === 0) return [];
  
  // Generate 8 sample rows with realistic mock data based on field names
  const sampleRows: Record<string, any>[] = [];
  const sampleInspectors = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Williams'];
  const sampleStatuses = ['Completed', 'In Progress', 'Scheduled', 'Cancelled'];
  const sampleEntityNames = ['Smith Property', 'Jones Residence', 'Green Building', 'Oak Street Complex'];
  
  for (let i = 0; i < 8; i++) {
    const row: Record<string, any> = {};
    
    for (const field of fields) {
      const fieldLower = field.toLowerCase();
      
      // Generate sample data based on field name patterns
      if (fieldLower.includes('inspector') && fieldLower.includes('name')) {
        row[field] = sampleInspectors[i % sampleInspectors.length];
      } else if (fieldLower.includes('status')) {
        row[field] = sampleStatuses[i % sampleStatuses.length];
      } else if (fieldLower.includes('date') || fieldLower.includes('scheduled')) {
        const date = new Date();
        date.setDate(date.getDate() - (7 - i)); // Last 8 days
        row[field] = date.toISOString().split('T')[0];
      } else if (fieldLower.includes('entity') && fieldLower.includes('name')) {
        row[field] = sampleEntityNames[i % sampleEntityNames.length];
      } else if (fieldLower.includes('address')) {
        row[field] = `${100 + i * 10} ${['Oak', 'Pine', 'Elm', 'Maple'][i % 4]} St`;
      } else if (fieldLower.includes('count') || fieldLower.includes('total')) {
        row[field] = Math.floor(Math.random() * 10);
      } else if (fieldLower.includes('violation')) {
        row[field] = Math.floor(Math.random() * 5);
      } else {
        // Default: show sample text
        row[field] = `Sample ${i + 1}`;
      }
    }
    
    sampleRows.push(row);
  }
  
  return sampleRows;
}
